import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeatureWithUsage } from '../../type/feature-types';
import Icon from '../icon/Icon';
import AuthContext from '../../contexts/authContext';
import { UserType } from '../../common/data/dummyRolesData';
import subscriptionService from '../../services/api/subscriptionService';

// Lazy import to avoid circular dependency
let useOrganization: () => { selectedOrganization: { id: string } | null };
try {
	useOrganization = require('../../contexts/organizationContext').useOrganization;
} catch {
	useOrganization = () => ({ selectedOrganization: null });
}

interface UpgradePromptProps {
	/** Feature that triggered the upgrade prompt */
	feature?: FeatureWithUsage;
	/** Reason for showing the prompt */
	reason?: 'disabled' | 'limit_reached';
	/** Visual variant */
	variant?: 'inline' | 'banner' | 'card' | 'modal';
	/** Callback when upgrade button is clicked */
	onUpgrade?: () => void;
	/** Custom message override */
	message?: string;
}

/**
 * Displays an upgrade call-to-action when a feature is not available.
 * Can be used inline, as a banner, or in a card format.
 *
 * @example
 * ```tsx
 * <UpgradePrompt
 *   feature={feature}
 *   reason="disabled"
 *   variant="card"
 * />
 * ```
 */
const UpgradePrompt: React.FC<UpgradePromptProps> = ({
	feature,
	reason = 'disabled',
	variant = 'inline',
	onUpgrade,
	message,
}) => {
	const { t } = useTranslation(['features', 'subscription', 'common']);
	const navigate = useNavigate();
	const { userData } = useContext(AuthContext);
	const [redirecting, setRedirecting] = useState(false);

	// Try to get organization context (only available in company pages)
	let organizationId: string | null = null;
	try {
		const { selectedOrganization } = useOrganization();
		organizationId = selectedOrganization?.id || null;
	} catch {
		organizationId = null;
	}

	const handleUpgrade = useCallback(async () => {
		if (onUpgrade) {
			onUpgrade();
			return;
		}

		// Determine if user is company or freelance and redirect accordingly
		const isCompany = userData?.role === UserType.COMPANY;
		const isFreelance = userData?.role === UserType.FREELANCE;

		try {
			setRedirecting(true);

			if (isCompany && organizationId) {
				// Redirect to Stripe Portal for organization
				await subscriptionService.redirectToOrganizationPortal(organizationId);
			} else if (isFreelance && userData?.id) {
				// Redirect to Stripe Portal for talent
				await subscriptionService.redirectToTalentPortal(userData.id);
			} else {
				// Fallback: navigate to subscription page
				navigate('/company/subscription');
			}
		} catch (err) {
			console.error('Failed to redirect to portal:', err);
			setRedirecting(false);
			// Fallback to subscription page on error
			navigate('/company/subscription');
		}
	}, [onUpgrade, userData, organizationId, navigate]);

	const getTitle = () => {
		if (reason === 'limit_reached') {
			return t('features.upgrade.limitReachedTitle', 'Limit Reached');
		}
		return t('features.upgrade.upgradeTitle', 'Upgrade Required');
	};

	const getMessage = () => {
		if (message) return message;

		const featureName = feature?.name ?? t('features.upgrade.thisFeature', 'This feature');

		if (reason === 'limit_reached') {
			return t('features.upgrade.limitReached', {
				feature: featureName,
				defaultValue: `You've used all your ${featureName} for this month. Upgrade to get more.`,
			});
		}

		return t('features.upgrade.notAvailable', {
			feature: featureName,
			defaultValue: `${featureName} is available on Plus plan. Upgrade to unlock this feature.`,
		});
	};

	const getBtnText = () => {
		return t('subscription.manageSubscription', 'Manage Subscription');
	};

	// Inline variant - minimal, fits in small spaces
	if (variant === 'inline') {
		return (
			<div className='d-flex align-items-center gap-2 text-muted p-2 bg-light rounded'>
				<Icon icon='Lock' size='lg' />
				<span className='small'>{getMessage()}</span>
				<button
					type='button'
					className='btn btn-sm btn-outline-primary ms-auto'
					onClick={handleUpgrade}
					disabled={redirecting}>
					{redirecting ? (
						<span className='spinner-border spinner-border-sm' />
					) : (
						getBtnText()
					)}
				</button>
			</div>
		);
	}

	// Banner variant - full width, attention grabbing
	if (variant === 'banner') {
		return (
			<div className='alert alert-warning d-flex align-items-center justify-content-between mb-0'>
				<div className='d-flex align-items-center gap-2'>
					<Icon icon='Warning' size='lg' />
					<span>{getMessage()}</span>
				</div>
				<button
					type='button'
					className='btn btn-warning'
					onClick={handleUpgrade}
					disabled={redirecting}>
					{redirecting ? (
						<span className='spinner-border spinner-border-sm' />
					) : (
						getBtnText()
					)}
				</button>
			</div>
		);
	}

	// Card variant - centered, for replacing content
	if (variant === 'card') {
		return (
			<div className='card border-0 shadow-sm'>
				<div className='card-body text-center py-5'>
					<div className='mb-3'>
						<Icon icon='Lock' size='3x' className='text-muted' />
					</div>
					<h5 className='card-title'>{getTitle()}</h5>
					<p className='card-text text-muted mb-4'>{getMessage()}</p>
					<button
						type='button'
						className='btn btn-primary btn-lg'
						onClick={handleUpgrade}
						disabled={redirecting}>
						{redirecting ? (
							<>
								<span className='spinner-border spinner-border-sm me-2' />
								{t('subscription.redirecting', 'Redirecting...')}
							</>
						) : (
							getBtnText()
						)}
					</button>
				</div>
			</div>
		);
	}

	// Modal variant - for use inside modals
	return (
		<div className='text-center p-4'>
			<div className='mb-3'>
				<Icon icon='Lock' size='3x' className='text-warning' />
			</div>
			<h4>{getTitle()}</h4>
			<p className='text-muted mb-4'>{getMessage()}</p>
			<button
				type='button'
				className='btn btn-primary'
				onClick={handleUpgrade}
				disabled={redirecting}>
				{redirecting ? (
					<>
						<span className='spinner-border spinner-border-sm me-2' />
						{t('subscription.redirecting', 'Redirecting...')}
					</>
				) : (
					getBtnText()
				)}
			</button>
		</div>
	);
};

export default UpgradePrompt;
